import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation, matchPath } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Pricing from "@/pages/Pricing";
import Demo from "@/pages/Demo";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import AppShell from "@/components/AppShell";
import Dashboard from "@/pages/Dashboard";
import Offer from "@/pages/Offer";
import Strategy from "@/pages/Strategy";
import Leads from "@/pages/Leads";
import Contacts from "@/pages/Contacts";
import ContactPage from "@/pages/ContactPage";
import Campaigns from "@/pages/Campaigns";
import CampaignPage from "@/pages/CampaignPage";
import Approvals from "@/pages/Approvals";
import Inbox from "@/pages/Inbox";
import Meetings from "@/pages/Meetings";
import Pipeline from "@/pages/Pipeline";
import Compliance from "@/pages/Compliance";
import Billing from "@/pages/Billing";
import Integrations from "@/pages/Integrations";
import Team from "@/pages/Team";
import LinkedInTasks from "@/pages/LinkedInTasks";
import Social from "@/pages/Social";
import Calendar from "@/pages/Calendar";
import AdminOverview from "@/pages/AdminOverview";
import Workspaces from "@/pages/Workspaces";
import Audit from "@/pages/Audit";
import Profile from "@/pages/Profile";
import OpsShell from "@/pages/OpsShell";
import OpsOrgs from "@/pages/OpsOrgs";
import OpsRequests from "@/pages/OpsRequests";
import OpsDemos from "@/pages/OpsDemos";
import NotFound from "@/pages/NotFound";
import NotAllowed from "@/pages/NotAllowed";

const TITLES = [
  ["/pricing", "Pricing"],
  ["/demo", "Request a demo"],
  ["/legal/terms", "Terms of Service"],
  ["/legal/privacy", "Privacy Policy"],
  ["/login", "Sign in"],
  ["/register", "Create workspace"],
  ["/payment/success", "Payment complete"],
  ["/payment/cancel", "Payment cancelled"],
  ["/app", "Command Center"],
  ["/app/offer", "Offer"],
  ["/app/strategy", "Strategy"],
  ["/app/leads", "Leads"],
  ["/app/contacts", "Contacts"],
  ["/app/contacts/:id", "Contact"],
  ["/app/campaigns", "Campaigns"],
  ["/app/campaigns/:id", "Campaign"],
  ["/app/approval", "Approval"],
  ["/app/inbox", "Inbox"],
  ["/app/pipeline", "Pipeline"],
  ["/app/calendar", "Calendar"],
  ["/app/social", "Social"],
  ["/app/tasks", "Tasks"],
  ["/app/meetings", "Meetings"],
  ["/app/admin", "Admin Overview"],
  ["/app/admin/team", "Team"],
  ["/app/admin/workspaces", "Workspaces"],
  ["/app/admin/billing", "Billing"],
  ["/app/admin/sending", "Sending"],
  ["/app/admin/audit", "Audit"],
  ["/app/settings/integrations", "Integrations"],
  ["/app/settings/profile", "Profile"],
  ["/ops", "Organizations"],
  ["/ops/requests", "Demo requests"],
  ["/ops/demos", "Demo orgs"],
];

function TitleManager() {
  const location = useLocation();
  useEffect(() => {
    if (location.pathname === "/") { document.title = "DealsOps — B2B Sales Control Plane"; return; }
    const match = TITLES.find(([p]) => matchPath({ path: p, end: true }, location.pathname));
    document.title = match ? `${match[1]} · DealsOps` : "Not found · DealsOps";
  }, [location.pathname]);
  return null;
}

function Protected({ children }) {
  const { user, checked } = useAuth();
  const location = useLocation();
  if (!checked) return <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground font-mono text-sm">Loading control plane…</div>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return children;
}

function AdminRoute() {
  const { user } = useAuth();
  const isAdmin = user?.role === "owner" || user?.role === "admin";
  return isAdmin ? <Outlet /> : <NotAllowed />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <TitleManager />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/demo" element={<Demo />} />
          <Route path="/legal/terms" element={<Terms />} />
          <Route path="/legal/privacy" element={<Privacy />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />

          <Route path="/app" element={<Protected><AppShell /></Protected>}>
            <Route index element={<Dashboard />} />
            <Route path="offer" element={<Offer />} />
            <Route path="strategy" element={<Strategy />} />
            <Route path="leads" element={<Leads />} />
            <Route path="contacts" element={<Contacts />} />
            <Route path="contacts/:id" element={<ContactPage />} />
            <Route path="campaigns" element={<Campaigns />} />
            <Route path="campaigns/:id" element={<CampaignPage />} />
            <Route path="approval" element={<Approvals />} />
            <Route path="inbox" element={<Inbox />} />
            <Route path="pipeline" element={<Pipeline />} />
            <Route path="calendar" element={<Calendar />} />
            <Route path="social" element={<Social />} />
            <Route path="tasks" element={<LinkedInTasks />} />
            <Route path="meetings" element={<Meetings />} />

            <Route path="admin" element={<AdminRoute />}>
              <Route index element={<AdminOverview />} />
              <Route path="team" element={<Team />} />
              <Route path="workspaces" element={<Workspaces />} />
              <Route path="billing" element={<Billing />} />
              <Route path="sending" element={<Compliance />} />
              <Route path="audit" element={<Audit />} />
            </Route>

            <Route path="settings/integrations" element={<Integrations />} />
            <Route path="settings/profile" element={<Profile />} />

            {/* Backward-compatible redirects for old paths */}
            <Route path="dashboard" element={<Navigate to="/app" replace />} />
            <Route path="approvals" element={<Navigate to="/app/approval" replace />} />
            <Route path="linkedin-tasks" element={<Navigate to="/app/tasks" replace />} />
            <Route path="team" element={<Navigate to="/app/admin/team" replace />} />
            <Route path="billing" element={<Navigate to="/app/admin/billing" replace />} />
            <Route path="compliance" element={<Navigate to="/app/admin/sending" replace />} />
            <Route path="integrations" element={<Navigate to="/app/settings/integrations" replace />} />

            <Route path="*" element={<NotFound />} />
          </Route>

          <Route path="/ops" element={<Protected><OpsShell /></Protected>}>
            <Route index element={<OpsOrgs />} />
            <Route path="requests" element={<OpsRequests />} />
            <Route path="demos" element={<OpsDemos />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
